import dayjs from '../../utils/dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SkeletonLoading from 'components/skeletonPageLoading';
import { Text, Card, DataTable, Link, Page, Badge, InlineStack } from '@shopify/polaris';
import __, { ___ } from 'languages/index';
import __helpers from 'helpers/index';

import Pagination from 'components/pagination';
import { CALL_STATUS, CALL_STATUS_KEY, DIRECTION_KEY, TypedPhoneLog, useGetPhonelogs } from 'queries/phonelog.query';
import AudioPlayer from './components/audio-player';
import { PhoneInIcon, PhoneOutIcon } from '@shopify/polaris-icons';
import CustomerNew from 'entities/customer/customer.new';
import CustomerSocialAssignedModal from 'entities/customer/customer_social_assigned.modal';
import SimpleFilter from 'components/simpleFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

export default function PhonelogView() {
  const rootSearchPath = window.location.search;

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let StringQuery: any = __helpers.ExtractUrl(rootSearchPath) || false;

  const initialQuery = {
    query: '',
    page: 1,
    limit: 40,
    sort: 'createdAt:desc',
    disposition: '',
  };

  const [phonelogData, setPhonelogData] = useState<TypedPhoneLog[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [idPlaying, setIdPlaying] = useState(false);
  const currentlyPlayingRef = useRef(null); // Lưu tham chiếu tới audio đang phát

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const {
    refetch: getEntities,
    data,
    isLoading: loading,
  } = useGetPhonelogs({
    sort: 'createdAt:desc',
    limit: 20,
    ...mainQuery,
  });

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  const renderItem = (phonelogData: TypedPhoneLog) => {
    if (__helpers.isEmpty(phonelogData)) return;
    let {
      id,
      destination,
      answeredBy,
      createdAt,
      duration,
      disposition,
      source,
      source_name,
      recordingfile,
      direction,
      destination_data,
      source_data,
    } = phonelogData;

    const isInbound = direction === DIRECTION_KEY.INBOUND;
    let tone;
    switch (disposition) {
      case CALL_STATUS_KEY.MISSED:
      case CALL_STATUS_KEY.FAILED:
        tone = 'critical';
        break;
      case CALL_STATUS_KEY.BUSY:
        tone = 'warning';
        break;
      case CALL_STATUS_KEY.NO_ANSWER:
        tone = 'magic';
        break;
      case CALL_STATUS_KEY.UNKNOWN:
      case CALL_STATUS_KEY.ANSWERING:
        tone = 'attention';
        break;

      default:
        tone = 'success';
        break;
    }

    return [
      <div style={{ minWidth: '100px' }}>
        <InlineStack blockAlign="center" gap={'200'} as="div">
          <Text tone="subdued" as="span">
            {isInbound ? <PhoneInIcon width={20} height={20} /> : <PhoneOutIcon width={20} height={20} />}
          </Text>
          <Badge tone={tone}>{CALL_STATUS?.[disposition] || ''}</Badge>
        </InlineStack>
      </div>,

      <Text as="p">
        {source_data ? (
          <div>
            <Link removeUnderline url={`/customer/view/${source_data.user_id}`}>
              {source_data.display_name || source_data.user_phonenumber}
            </Link>
          </div>
        ) : (
          <Link
            onClick={() => {
              currentName.current = source_name || '';

              if (!source_name) {
                currentNumber.current = source || '';
                setShowNewCustomer(true);
              } else {
                currentSocialName.current = 'zalo';
                currentSocialId.current = source || '';
                setShowSocialAssigned(true);
              }
            }}
            removeUnderline
          >
            <Text as="p" fontWeight="medium" tone="success">
              <FontAwesomeIcon icon={faPlus} /> {source_name ? 'Gán vào danh bạ' : 'Thêm vào danh bạ'}
            </Text>
          </Link>
        )}

        {source_name ? (
          <Text as="p" variant="bodySm" tone="subdued">
            {source_name}
          </Text>
        ) : null}

        <Text as="h4" variant="headingMd" tone="success">
          {source || '--'}
        </Text>
      </Text>,

      <Text as="p">
        {destination_data ? (
          <div>
            <Link removeUnderline url={`/customer/view/${destination_data.user_id}`}>
              {destination_data.display_name || destination_data.user_phonenumber}
            </Link>
          </div>
        ) : (
          <Link
            onClick={() => {
              currentNumber.current = destination || '';
              currentName.current = '';
              currentSocialName.current = '';
              currentSocialId.current = '';
              setShowNewCustomer(true);
            }}
            removeUnderline
          >
            <Text as="p" fontWeight="medium" tone="success">
              <FontAwesomeIcon icon={faPlus} />
              Thêm vào danh bạ
            </Text>
          </Link>
        )}
        <Text as="h4" variant="headingMd" tone="success">
          {destination || ''}
        </Text>
      </Text>,

      <Text as="p">{dayjs(new Date(Number(createdAt))).format('DD-MM-YYYY HH:mm')}</Text>,
      <AudioPlayer
        id={id}
        duration={duration}
        idPlaying={idPlaying}
        setIdPlaying={setIdPlaying}
        currentlyPlayingRef={currentlyPlayingRef}
        audioUrl={recordingfile || ''}
      />,
      <Text fontWeight="bold" as="p">
        {answeredBy ? (
          answeredBy
        ) : (
          <>
            <Badge></Badge>
            <Badge></Badge>
          </>
        )}
      </Text>,
    ];
  };

  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  useEffect(() => {
    try {
      let { body, totalItems } = data;
      setPhonelogData(body);
      setTotalItems(totalItems);
    } catch (e) {}
  }, [data]);

  useEffect(() => {
    getEntities();
  }, [mainQuery]);

  useEffect(() => {
    let buildURLSearch = __helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'Product', '/phonelog' + buildURLSearch);
    }
  }, [mainQuery]);

  const [showNewCustomer, setShowNewCustomer] = useState<boolean>(false);
  const [showSocialAssigned, setShowSocialAssigned] = useState(false);

  const currentNumber = useRef('');
  const currentName = useRef('');
  const currentSocialName = useRef('');
  const currentSocialId = useRef('');

  const closeNewCustomerModal = useCallback(() => {
    // load laij duwx lieeuj
    setShowNewCustomer(false);
    currentNumber.current = '';
    currentName.current = '';
    currentSocialName.current = '';
    currentSocialId.current = '';
  }, []);

  const closeSocialAssignedModal = useCallback(
    (assigned?: boolean) => {
      setShowSocialAssigned(false);
      currentName.current = '';
      currentSocialName.current = '';
      currentSocialId.current = '';
      if (assigned) getEntities();
    },
    [getEntities]
  );

  return (
    <>
      <Helmet>
        <title>Nhật ký cuộc gọi</title>
      </Helmet>
      {phonelogData === null ? (
        <SkeletonLoading fullWidth />
      ) : (
        <Page fullWidth title="Nhật ký cuộc gọi" compactTitle>
          <Card padding="0">
            <SimpleFilter
              loading={loading}
              onCallback={filterCallback}
              options={[
                {
                  label: 'GỌI NHỠ',
                  field: 'disposition',
                  value: 'MISSED',
                },
                {
                  label: 'BẬN',
                  field: 'disposition',
                  value: 'BUSY',
                },
                {
                  label: 'KHÔNG TRẢ LỜI',
                  field: 'disposition',
                  value: 'NO ANSWER',
                },
              ]}
              sortField={[]}
            />
            <DataTable
              verticalAlign="middle"
              sortable={[false, false, false, false, false, false, false, false]}
              defaultSortDirection="descending"
              initialSortColumnIndex={6}
              onSort={null}
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['', 'Gọi từ', 'Gọi đến', 'Thời gian', 'Ghi âm', 'Extension']}
              rows={phonelogData?.map(renderItem)}
              hideScrollIndicator
              hasZebraStripingOnData
              increasedTableDensity
              stickyHeader={false} /** Đừng dính cái heading lại, toang đấy! */
              footerContent={___('Show page {display_page_number} of {total_record_number} results', {
                display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
                total_record_number: <Text as="strong">{totalItems}</Text>,
              })}
            />
          </Card>

          <br />
          {totalItems > 0 ? (
            <Pagination
              TotalRecord={totalItems}
              onChange={onChangePagination}
              pageSize={Number(mainQuery?.limit)}
              activeCurrentPage={Number(mainQuery?.page)}
            />
          ) : null}
        </Page>
      )}

      {showNewCustomer ? (
        <CustomerNew
          onClose={closeNewCustomerModal}
          show
          defaultNumber={currentNumber.current}
          defaultName={currentName.current}
          defaultSocialName={currentSocialName.current}
          defaultSocialSlug={currentSocialId.current}
        />
      ) : null}

      {showSocialAssigned ? (
        <CustomerSocialAssignedModal
          show
          socialName={currentSocialName.current}
          socialSlug={currentSocialId.current}
          onClose={closeSocialAssignedModal}
        />
      ) : null}
    </>
  );
}

